import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './EnvironmentDropdown.scss';
// import CheckBox from '@root/components/v2/ui-elements/CheckBox';
import { Box } from '@mui/material';
import EnvironmentDropdownOption from './EnvironmentDropdownOption';
import SearchIcon from '@root/assets/svg/Navigation/SearchIcon';
import { X as ClearIcon } from 'lucide-react';
import { useTheme } from '@root/context/ThemeContext';
import { useCloudProvider } from '../../content/CloudProviderBar/CloudProviderContext';
import { useSelectedEnvironments } from '@root/hooks/environments';
import AzureIcon from '@root/assets/img/header/cloud_providers/azure.png';
// import GCPIcon from '@root/assets/img/header/cloud_providers/gcp.png';
import aws_light from '@root/assets/img/complainceFamilyIcons/AWS SVG.svg';
import gcp_well from '@root/assets/img/complainceFamilyIcons/GCP-Well-Architected_v2020-07-02.svg';

const EnvironmentDropdown = ({
  allOptions,
  value,
  setValue,
  disabled,
  isLoading,
  disabledOptions,
  singleSelect,
  onChange,
}) => {
  //   const options = [
  //     { label: 'Drift', children: ['Create', 'Modify', 'Delete'] },
  //     { label: 'Threat' },
  //     { label: 'Remediation', children: ['One Click / Manual', 'Auto Remediation'] },
  //   ];

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const ref = useRef(null);
  const { selectedEnvironments } = useSelectedEnvironments();
  const { isDarkMode } = useTheme();
  const { cloudProvider, handleChangeCloudProvider } = useCloudProvider();
  const options = useMemo(() => {
    const lowerCaseSearchTerm = searchValue.toLowerCase();

    // Map through the options array to filter based on the search term
    return allOptions
      .map(provider => {
        const filteredOptions = provider.options.filter(option =>
          option.value.toLowerCase().includes(lowerCaseSearchTerm),
        );

        // Only return providers that have matching options
        if (filteredOptions.length > 0) {
          return {
            ...provider,
            options: filteredOptions,
          };
        }

        return null; // Exclude providers with no matching options
      })
      .filter(provider => provider !== null);
  }, [allOptions, searchValue]);

  // console.log(options, 'options');

  const isAllSelected = useMemo(() => {
    if (!value || !value.length) return false;

    return options.every(option => option.options.every(o => value.includes(o.value)));
  }, [options, value]);

  const handleClick = useCallback(
    e => {
      if (
        e?.currentTarget?.dataset?.cloudProvider === 'all' ||
        selectedEnvironments.some(item => item.cloudProvider === e?.currentTarget?.dataset?.cloudProvider)
      ) {
        handleChangeCloudProvider(e?.currentTarget?.dataset?.cloudProvider);
      }
    },
    [handleChangeCloudProvider, selectedEnvironments],
  );
  const awsEnvironments = useMemo(() => {
    return selectedEnvironments.filter(item => item.cloudProvider === 'aws');
  }, [selectedEnvironments]);

  const gcpEnvironments = useMemo(() => {
    return selectedEnvironments.filter(item => item.cloudProvider === 'gcp');
  }, [selectedEnvironments]);

  const azureEnvironments = useMemo(() => {
    return selectedEnvironments.filter(item => item.cloudProvider === 'azure');
  }, [selectedEnvironments]);

  // const updatedOptions = useMemo(() => {
  //   return options.filter(option => option.label.toLowerCase() === cloudProvider);
  // }, [options, cloudProvider]);
  // console.log(updatedOptions, options, 'updatedOptions', cloudProvider);

  const handleClear = useCallback(() => setSearchValue(''), []);
  const handleOptionClick = useCallback(
    option => {
      if (option === 'all') {
        if (isAllSelected) {
          onChange([]);
        } else {
          const selectedValues = [];
          for (const g of options) {
            for (const o of g.options) {
              if (!disabledOptions.includes(o.value)) {
                selectedValues.push(o.value);
              }
            }
          }
          onChange(selectedValues);
        }
      } else {
        if (value.includes(option.value)) {
          onChange(value.filter(v => v !== option.value));
        } else {
          onChange([...value, option.value]);
        }
      }
    },
    [isAllSelected, onChange, options, disabledOptions, value],
  );

  const handleGroupClick = useCallback(
    group => {
      const isAllOptionsInGroupSelected = group.options.every(o => value.includes(o.value));

      if (isAllOptionsInGroupSelected) {
        // Remove all options in group from value
        onChange(value.filter(v => !group.options.find(o => o.value === v)));
      } else {
        // Add all options in the group to value
        onChange([
          ...value,
          ...group.options
            .filter(o => !value.includes(o.value) && !disabledOptions.includes(o.value))
            .map(o => o.value),
        ]);
      }
    },
    [value, onChange, disabledOptions],
  );

  const handleOutsideClick = e => {
    if (ref.current && !ref.current.contains(e.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  });

  // const handleOnOptionSelect = option => {
  //   //check if selected options contains the passed option. if yes, remove it from selected options. otherwise add
  //   if (value.some(op => op.value === option.value)) {
  //     setValue(prev => prev.filter(op => op.value !== option.value));
  //   } else {
  //     setValue(prev => [...prev, option]);
  //   }
  // };

  return (
    <div className="EnvironmentDropdown__Container" ref={ref}>
      <Box
        className="EnvironmentDropdown__ValueContainer"
        onClick={() => setOpen(prev => !prev)}
        style={{ pointerEvents: disabled || isLoading ? 'none' : '', marginLeft: '12.5px' }}>
        <input
          className="EnvironmentDropdown__ValueInput"
          onChange={() => {}}
          value={
            !value.length || isAllSelected
              ? `Environments: All`
              : `Environments: ${value.length > 0 ? `+${value.length}` : 'All'}`
          }
        />
        {/* <div style={{ flexGrow: '1' }} /> */}
        <Box
          onClick={() => setSearchValue('')}
          className="EnvironmentDropdown__DropdownArrow"
          style={{ transform: open ? 'rotate(180deg)' : '', transition: 'all 200ms', height: '23px', color: '#fff' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M-3.1883e-08 9.7294C-2.31213e-08 9.52895 0.0703583 9.35078 0.211075 9.21715C0.492508 8.92762 0.985017 8.92762 1.26645 9.21715L4.50293 12.2684L7.71596 9.21715C7.99739 8.92762 8.4899 8.92762 8.77134 9.21715C9.07622 9.48441 9.07622 9.95212 8.77134 10.2194L5.01889 13.7829C4.73746 14.0724 4.24495 14.0724 3.96352 13.7829L0.211075 10.2194C0.0703582 10.0857 -3.96713e-08 9.90757 -3.1883e-08 9.7294Z"
              fill="#fff"></path>
          </svg>
        </Box>
      </Box>

      <div className="EnvironmentDropdown__Options " style={{ display: open ? 'block' : 'none' }}>
        <div
          style={{
            display: 'flex',
            borderRadius: '5px',
            padding: '4px',
            gap: '8px',
            '& img': {
              filter: isDarkMode ? 'brightness(0.8)' : 'none',
            },
          }}>
          <div
            className={`EnvironmentDropdown__CloudProviderBar__Option ${cloudProvider === 'all' ? 'Active' : ''}`}
            data-cloud-provider="all"
            onClick={handleClick}

            // onClick={()=> selectedEnvironments.length === allEnvironments.length ?  handleClick():''}
          >
            <div className="font-normal text-[15px] flex items-center">
              All
              <span className="font-normal text-[11px] ml-1">({selectedEnvironments.length})</span>
            </div>
          </div>

          <div
            className={`EnvironmentDropdown__CloudProviderBar__Option flex items-center ${
              cloudProvider === 'aws' ? 'Active' : ''
            }`}
            data-cloud-provider="aws"
            onClick={handleClick}>
            <img src={aws_light} style={{ height: '24px', width: '24px' }} />
            <div className="font-normal text-[11px]">({awsEnvironments.length})</div>
          </div>

          <div
            className={`EnvironmentDropdown__CloudProviderBar__Option flex items-center ${
              cloudProvider === 'gcp' ? 'Active' : ''
            }`}
            data-cloud-provider="gcp"
            onClick={handleClick}>
            <img src={gcp_well} style={{ height: '20px', width: '20px' }} />
            <div className="font-normal text-[11px]">({gcpEnvironments.length})</div>
          </div>

          <div
            className={`EnvironmentDropdown__CloudProviderBar__Option flex items-center ${
              cloudProvider === 'azure' ? 'Active' : ''
            }`}
            data-cloud-provider="azure"
            onClick={handleClick}>
            <img src={AzureIcon} style={{ height: '16px', width: '20px' }} />
            <div className="font-normal text-[11px]"> ({azureEnvironments.length})</div>
          </div>
        </div>

        <div className="Environment__search">
          <SearchIcon className="search-icon-env" />
          <input
            type="text"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="Environment__search-input"
            placeholder="Search..."
          />
          {searchValue && <ClearIcon size={20} className="Environment__clear-icon" onClick={handleClear} />}
        </div>
        <div style={{ maxHeight: '217px', overflowX: 'hidden', overflowY: 'auto' }} className="CustomScrollbar">
          {options.length > 0 ? (
            <EnvironmentDropdownOption
              // key={index}
              options={options}
              // index={index}
              length={options.length}
              onClick={onChange}
              // selected={value.some(op => op === option.value)}
              isAllSelected={isAllSelected}
              handleOptionClick={handleOptionClick}
              disabledOptions={disabledOptions}
              handleGroupClick={handleGroupClick}
              value={value}
              setValue={setValue}
              singleSelect={singleSelect}
              cloudProvider={cloudProvider}
            />
          ) : (
            <p
              style={{
                fontFamily: 'Manrope',
                fontWeight: '500',
                fontSize: '13px',
                padding: '20px 0px',
                textAlign: 'center',
                // borderTop: '1px solid #cbd5e0',
              }}>
              {' '}
              No Environment found
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvironmentDropdown;
